import React, { useState, Fragment, useContext, useEffect } from 'react';
import {
  Box,
  Paper,
  Divider,
  Tooltip,
  IconButton,
  Hidden,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import CottageIcon from '../../../../static/outageIconsV2/cottage.svg';
import MyLocationIcon from '../../../../static/outageIconsV2/my_location.svg';
import HelpIcon from '../../../../static/outageIconsV2/help.svg';
import MapLegendIcon from '../../../../static/outageIconsV2/map_legend.svg';
import ArrowDropDownCircleIcon from '../../../../static/outageIconsV2/arrow_drop_down_circle.svg';
import ArrowUpCircleIcon from '../../../../static/outageIconsV2/arrow_up_circle.svg';
import PSPSEventIcon from '../icons/PSPSEventIcon';
import colors from '../../../../themes/main-colors';
import { HelpTipsDialog } from '../help-tips-dialog';
import { OutageMapDialog } from '../outage-map-dialog';
import { Position } from '../../types';
import { useAccountDetailsList } from '../../hooks/useAccountDetailsList';
import useAuth from '../../../../hooks/useAuth';
import { useOutageLeftPanelState } from '../../providers/OutageLeftPanelStateProvider';
import { SwitchAccountDialog } from '../switch-account-dialog';
import SignInModal from '../../../sign-in-form/SignInModal';
import routes from '../../../../routes';
import { useTranslation } from '../../../../hooks/useTranslation';
import { isLocationWithinPGEServiceBoundary } from '../../../../util/map-utils';
import { NotificationsContext } from '../../../../providers/NotificationsProvider';
import { featureFlagsContext } from '../../../../providers/FeatureFlagsProvider';
import { useOutageMapOptions } from '../../providers/OutageMapOptionsProvider';
import { CONSTANTS } from '../../constants';
import { getFromStorage, setInStorage } from '../../../../util/storage-utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mainArea: {
      position: 'absolute',
      right: 20,
      marginTop: 20,
      backgroundColor: '#fff',
      borderRadius: 48,
      zIndex: 1200,
    },
    iconButton: {
      padding: 10,
      '&:hover': {
        backgroundColor: colors.white,
      },
    },
    tooltip: {
      backgroundColor: colors.white,
      color: colors.noirBlur,
      fontSize: '1rem',
      boxShadow: '0px 0px 4px 0px #00000040',
    },
  }),
);

interface Props {
  setCurrentUserLocation: (pos: Position) => void;
}

export const OutageMapLegends = ({ setCurrentUserLocation }: Props) => {
  const [openLegendPanel, setOpenLegendPanel] = useState(true);
  const [openHelpTipsDialog, setOpenHelpTipsDialog] = React.useState(false);
  const [openOutageMapDialog, setOpenOutageMapDialog] = React.useState(false);
  const classes = useStyles();
  const { t } = useTranslation();
  const notification = useContext(NotificationsContext);
  const { isAuthenticated } = useAuth();
  const [isLoginModalVisible, setIsLoginModalVisible] = useState<boolean>(
    false,
  );
  const { setSelectedAccountForOutage } = useOutageLeftPanelState();
  const [openSwitchAccountDialog, setOpenSwitchAccountDialog] = useState(false);
  const {
    accountListWithOutageInfo,
    isSingleAccount,
    loading: accountListLoading,
  } = useAccountDetailsList();
  const { isOutagesPSPSEnabled, loading } = useContext(featureFlagsContext);
  const { outageToggles, setOutageToggles } = useOutageMapOptions();

  const showMessage = (message = '') => {
    notification.setState({
      isOpen: true,
      severity: 'info',
      message: message,
    });
  };

  //Show Help and Tips for the first time outages page view
  useEffect(() => {
    const isHelpViewed = getFromStorage(
      CONSTANTS.HELP_MODAL_VIEWED_STORAGE_KEY,
    );

    if (!isHelpViewed) {
      setOpenHelpTipsDialog(true);
      setInStorage(CONSTANTS.HELP_MODAL_VIEWED_STORAGE_KEY, 'true');
    }
  }, []);

  const handleMyServiceLocation = () => {
    if (!isAuthenticated) {
      setIsLoginModalVisible(true);
      return;
    }

    if (isSingleAccount && accountListWithOutageInfo.length > 0) {
      setSelectedAccountForOutage({ ...accountListWithOutageInfo[0] });
    }

    if (!isSingleAccount) {
      setOpenSwitchAccountDialog(true);
    }
  };

  const handleCurrentLocation = () => {
    void navigator.permissions
      .query({ name: 'geolocation' })
      .then(function(result) {
        if (result.state === 'denied') {
          // Permission has been permanently dismissed or blocked
          showMessage(t('ERROR_LOCATION_PERMISSION_DENIED'));
        } else if (result.state === 'prompt') {
          // Permission has not been requested or was dismissed once
          getCurrentUserLocation();
        } else if (result.state === 'granted') {
          // Permission has already been granted
          getCurrentUserLocation();
        }
      });
  };

  const getCurrentUserLocation = () => {
    navigator.geolocation?.getCurrentPosition(
      position => {
        const {
          latitude: userCurrentLatitude,
          longitude: userCurrentLongitude,
        } = position.coords;

        //Check if user location is within PGE service boundary
        if (
          isLocationWithinPGEServiceBoundary({
            lat: userCurrentLatitude,
            lng: userCurrentLongitude,
          })
        ) {
          setCurrentUserLocation({
            lat: userCurrentLatitude,
            lng: userCurrentLongitude,
          });
        } else {
          //User is located ouside of PGE territory
          showMessage(t('LOCATION_OUTSIDE_PGE_SERVICE_AREA'));
        }
      },
      error => {
        showMessage(t('ERROR_LOCATION_PERMISSION_DENIED'));
      },
    );
  };

  const handlePSPSZoneToggle = () => {
    setOutageToggles(prev => ({
      ...prev,
      psps: !prev.psps,
    }));
  };

  const LegendIcons = () => (
    <Fragment>
      <Box px={0.5} py={1}>
        <Box>
          <Tooltip
            title={t('OUTAGE_MY_SERVICE_LOCATION')}
            placement="left"
            classes={{ tooltip: classes.tooltip }}
          >
            <IconButton
              className={classes.iconButton}
              onClick={handleMyServiceLocation}
              disabled={accountListLoading}
            >
              <CottageIcon width={30} height={30} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box>
          <Tooltip
            title={t('OUTAGE_MY_CURRENT_LOCATION')}
            placement="left"
            classes={{ tooltip: classes.tooltip }}
          >
            <IconButton
              className={classes.iconButton}
              onClick={handleCurrentLocation}
            >
              <MyLocationIcon width={30} height={30} />
            </IconButton>
          </Tooltip>
        </Box>
        {!loading && isOutagesPSPSEnabled && (
          <Box>
            <Tooltip
              title={
                outageToggles.psps
                  ? t('OUTAGE_HIDE_PSPS_EVENTS')
                  : t('OUTAGE_SHOW_PSPS_EVENTS')
              }
              placement="left"
              classes={{ tooltip: classes.tooltip }}
            >
              <IconButton
                className={classes.iconButton}
                onClick={handlePSPSZoneToggle}
              >
                <PSPSEventIcon
                  size={30}
                  color={outageToggles.psps ? undefined : colors.noirBlur}
                  variant={outageToggles.psps ? 'filled' : 'outlined'}
                />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Box>
          <Tooltip
            title={t('OUTAGE_MAP_LAYERS_AND_LEGENDS')}
            placement="left"
            classes={{ tooltip: classes.tooltip }}
          >
            <IconButton
              className={classes.iconButton}
              onClick={() => setOpenOutageMapDialog(true)}
            >
              <MapLegendIcon width={30} height={30} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box>
          <Tooltip
            title={t('OUTAGE_HELP_AND_TIPS')}
            placement="left"
            classes={{ tooltip: classes.tooltip }}
          >
            <IconButton
              className={classes.iconButton}
              onClick={() => setOpenHelpTipsDialog(true)}
            >
              <HelpIcon width={30} height={30} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Fragment>
  );

  return (
    <Paper elevation={2} className={classes.mainArea}>
      {/* Mobile view */}
      <Hidden mdUp>
        {!openLegendPanel && (
          <Box
            onClick={() => setOpenLegendPanel(true)}
            style={{ cursor: 'pointer', display: 'flex', padding: 14 }}
          >
            <ArrowDropDownCircleIcon width={30} height={30} />
          </Box>
        )}

        {openLegendPanel && (
          <Fragment>
            <Box
              onClick={() => setOpenLegendPanel(false)}
              style={{ cursor: 'pointer', display: 'flex', padding: 14 }}
            >
              <ArrowUpCircleIcon width={30} height={30} />
            </Box>
            <Divider style={{ marginLeft: 4, marginRight: 4 }} />
            <LegendIcons />
          </Fragment>
        )}
      </Hidden>

      {/* Desktop view */}
      <Hidden smDown>
        <LegendIcons />
      </Hidden>

      {isAuthenticated && (
        <SwitchAccountDialog
          open={openSwitchAccountDialog}
          setOpen={setOpenSwitchAccountDialog}
          accountListWithOutageInfo={accountListWithOutageInfo}
        />
      )}

      {!isAuthenticated && (
        <SignInModal
          redirectUrl={routes.OUTAGES}
          isOpen={isLoginModalVisible}
          handleClose={() => setIsLoginModalVisible(false)}
        />
      )}

      <HelpTipsDialog
        open={openHelpTipsDialog}
        setOpen={setOpenHelpTipsDialog}
      />
      <OutageMapDialog
        open={openOutageMapDialog}
        setOpen={setOpenOutageMapDialog}
      />
    </Paper>
  );
};
